package com.anjuros.website.redesign.sections.faq

import androidx.compose.runtime.Composable
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.redesign.components.RdLink
import com.anjuros.website.redesign.components.RdTag
import com.anjuros.website.redesign.styles.FaqCardStyle
import com.anjuros.website.redesign.styles.FaqGroupHeadStyle
import com.anjuros.website.redesign.styles.FaqGroupStyle
import com.anjuros.website.redesign.styles.FaqGroupsStyle
import com.anjuros.website.redesign.styles.FaqLinksStyle
import com.anjuros.website.redesign.styles.FaqNoResultsStyle
import com.anjuros.website.redesign.styles.RdFaqListStyle
import com.anjuros.website.redesign.styles.RdH2Style
import com.anjuros.website.redesign.styles.RdWrapStyle
import com.anjuros.website.redesign.util.RdRoute
import com.anjuros.website.redesign.util.RdText
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** The questions grouped by topic, filtered by the search. */
@Composable
fun FaqTopicsSection(query: String) {
    val pattern = faqSearchPattern(query)
    // Each topic with only the questions that match the search; topics with none are left out.
    val shown = FaqTopics.map { topic ->
        topic to topic.faqs.filter { (question, answer) -> faqMatches(pattern, LocalText(question), LocalText(answer)) }
    }.filter { (_, faqs) -> faqs.isNotEmpty() }

    Div(RdWrapStyle.toModifier().toAttrs()) {
        Div(FaqGroupsStyle.toModifier().toAttrs()) {
            shown.forEach { (topic, faqs) -> TopicGroup(topic, faqs, pattern) }
            if (shown.isEmpty()) {
                P(FaqNoResultsStyle.toModifier().toAttrs()) {
                    Text(LocalText(RdText.FAQS_NO_RESULTS_START))
                    RdLink(RdRoute.CONTACT) { Text(LocalText(RdText.FAQS_NO_RESULTS_LINK)) }
                    Text(LocalText(RdText.FAQS_NO_RESULTS_END))
                }
            }
        }
    }
}

@Composable
private fun TopicGroup(topic: FaqTopic, faqs: List<Pair<RdText, RdText>>, pattern: Regex?) {
    val number = (FaqTopics.indexOf(topic) + 1).toString().padStart(2, '0')
    RdTag("section", FaqGroupStyle.toModifier().id(topic.id).attrsModifier { attr("aria-labelledby", "${topic.id}-h") }) {
        Div(FaqGroupHeadStyle.toModifier().toAttrs()) {
            Span({ attr("aria-hidden", "true") }) { Text(number) }
            H2(RdH2Style.toModifier().id("${topic.id}-h").toAttrs()) { Text(LocalText(topic.title)) }
        }
        Div(RdFaqListStyle.toModifier().then(FaqCardStyle.toModifier()).toAttrs()) {
            faqs.forEach { (question, answer) ->
                // While searching, the matching answers are opened so the highlighted words are visible.
                RdTag("details", Modifier.attrsModifier { if (pattern != null) attr("open", "") }) {
                    // One span, so the highlighted pieces stay together in the flex row with the + icon.
                    RdTag("summary") { Span { FaqHighlighted(LocalText(question), pattern) } }
                    P { FaqHighlighted(LocalText(answer), pattern) }
                }
            }
        }
        Div(FaqLinksStyle.toModifier().toAttrs()) {
            topic.links.forEach { (text, path) -> RdLink(path) { Text(LocalText(text)) } }
        }
    }
}
